import React, { useCallback, Fragment } from 'react';
// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
import { di } from 'react-magnetic-di';
import {
	type CommandPaletteModalProps,
	CommandPalette,
	type AnalyticsAttributes,
	useCommandPaletteIsOpen,
} from '@atlassian/jira-command-palette';
import { handleAnalytics } from '@atlassian/jira-command-palette-common';
import { useQuickSearchCommand } from '@atlassian/jira-command-palette-quick-search';
import { useCommandPaletteRegistry } from '@atlassian/jira-command-palette-registry';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { useIntl } from '@atlassian/jira-intl';
import { CONTAINER_TEST_ID } from '../common/constants';
import { useCloseDotMenu } from '../controllers/use-close-dot-menu';
import { useDisableCompalIfEmbed } from '../controllers/use-disable-compal-embed';
import { useRegisterDismissCommand } from '../controllers/use-register-dismiss-command';
import { useRegisterSections } from '../controllers/use-register-sections';
import { FooterFeedbackContent } from './footer-feedback-content';
import { useRegisterInitialCommands } from './initial-commands';
import { messages } from './messages';
import { RegisterDynamicCommands } from './register-dynamic-commands';

export const CommandPaletteIntegration = (props: CommandPaletteModalProps) => {
	// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
	di(ShortcutScope);
	const { formatMessage } = useIntl();
	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { quickSearchCommand } = useQuickSearchCommand({ keepSearch: true });

	useCommandPaletteRegistry();

	const onErrorAnalytics = useCallback(
		(
			attribution: string | undefined,
			error: Error | undefined,
			id: string,
			attributes?: AnalyticsAttributes,
		) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: `jira.commandPalette.${id}`,
					packageName: 'jiraCommandPaletteIntegration',
					teamName: attribution || 'deliveroo',
				},
				attributes,
			});
		},
		[],
	);

	useRegisterSections();

	useRegisterDismissCommand();

	useCloseDotMenu();

	useDisableCompalIfEmbed();

	useRegisterInitialCommands();

	const Wrapper = isOpen ? ShortcutScope : Fragment;
	return (
		<Wrapper>
			<div data-testid={CONTAINER_TEST_ID}>
				<CommandPalette
					defaultFooterContent={<FooterFeedbackContent />}
					onAnalytics={handleAnalytics}
					onErrorAnalytics={onErrorAnalytics}
					defaultPlaceholder={
						ff('compal-issue-actions-very-high-priority_f5w5r')
							? formatMessage(messages.defaultPlaceholder)
							: formatMessage(messages.defaultPlaceholderWithQuickSearch, {
									searchModeIcon: '"/"',
								})
					}
					defaultFallback={{
						onError: {
							label: formatMessage(messages.onErrorFallback),
						},
						onNoResults: {
							label: formatMessage(messages.noResultsFound),
						},
					}}
					rootFallback={{
						label: formatMessage(messages.noResultsFound),
						fallbackCommand: quickSearchCommand,
						fallbackSection: {
							name: formatMessage(messages.recommendedSection),
						},
					}}
					{...props}
				/>
				<RegisterDynamicCommands />
			</div>
		</Wrapper>
	);
};
